import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, orderBy, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { format } from 'date-fns';
import {
  MessageSquare, Bug, AlertCircle, Lightbulb, HelpCircle, Search,
} from 'lucide-react';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../context/AuthContext';
import { useUnreadFeedback } from '../../hooks/useUnreadFeedback';
import { useToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import './AdminFeedbackPage.css';

const TYPE_META = {
  bug:   { icon: Bug,          badge: 'feedback-type-bug' },
  issue: { icon: AlertCircle,  badge: 'feedback-type-issue' },
  idea:  { icon: Lightbulb,    badge: 'feedback-type-idea' },
  other: { icon: HelpCircle,   badge: 'feedback-type-other' },
};

const STATUS_META = {
  new:       { badge: 'feedback-status-new' },
  reviewing: { badge: 'feedback-status-reviewing' },
  planned:   { badge: 'feedback-status-planned' },
  fixed:     { badge: 'feedback-status-fixed' },
  closed:    { badge: 'feedback-status-closed' },
};

const PRIORITY_META = {
  low:    { badge: 'feedback-priority-low' },
  medium: { badge: 'feedback-priority-medium' },
  high:   { badge: 'feedback-priority-high' },
};

const STATUSES = Object.keys(STATUS_META);
const TYPES = Object.keys(TYPE_META);
const PRIORITIES = Object.keys(PRIORITY_META);

function useFeedback() {
  const { companyId } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.FEEDBACK),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [companyId]);

  return { items, loading };
}

function FeedbackDetail({ item, onClose }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState(item.status);
  const [adminNotes, setNotes] = useState(item.adminNotes ?? '');
  const [saving, setSaving] = useState(false);

  const isDirty = status !== item.status || adminNotes !== (item.adminNotes ?? '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.FEEDBACK, item.id), {
        status,
        adminNotes,
        updatedAt: serverTimestamp(),
      });
      toast(t('feedback.statusUpdated'), 'success');
      onClose();
    } catch {
      toast(t('feedback.updateFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const TypeIcon = TYPE_META[item.type]?.icon ?? HelpCircle;
  const created = item.createdAt?.toDate?.();

  return (
    <Modal
      title={t('feedback.detailTitle')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>{t('common.close')}</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? <span className="spinner" /> : t('feedback.saveChanges')}
          </button>
        </>
      }
    >
      <div className="fb-detail-header">
        <span className={`badge ${TYPE_META[item.type]?.badge ?? ''}`}>
          <TypeIcon size={11} />
          {t(`feedback.types.${item.type}`, { defaultValue: item.type })}
        </span>
        <span className={`badge ${PRIORITY_META[item.priority]?.badge ?? ''}`}>
          {t(`feedback.priorities.${item.priority}`, { defaultValue: item.priority })}
        </span>
      </div>

      <h3 className="fb-detail-title">{item.title}</h3>

      <div className="form-group">
        <label className="form-label">{t('feedback.description')}</label>
        <div className="fb-detail-desc">{item.description}</div>
      </div>

      <div className="fb-detail-meta">
        <div className="fb-detail-meta-row">
          <span className="fb-detail-meta-label">{t('feedback.submittedBy')}</span>
          <span>{item.submittedByName || '—'} ({item.submittedByRole})</span>
        </div>
        <div className="fb-detail-meta-row">
          <span className="fb-detail-meta-label">{t('feedback.page')}</span>
          <code className="fb-detail-page">{item.page}</code>
        </div>
        {created && (
          <div className="fb-detail-meta-row">
            <span className="fb-detail-meta-label">{t('users.createdAt')}</span>
            <span>{format(created, 'MMM d, yyyy h:mm a')}</span>
          </div>
        )}
        {item.userAgent && (
          <div className="fb-detail-meta-row">
            <span className="fb-detail-meta-label">{t('feedback.browser')}</span>
            <span className="fb-detail-ua">{item.userAgent}</span>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">{t('users.status')}</label>
        <select
          className="form-control"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`feedback.statuses.${s}`)}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">{t('feedback.adminNotes')}</label>
        <textarea
          className="form-control"
          value={adminNotes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('feedback.adminNotesPlaceholder')}
          rows={3}
          style={{ resize: 'vertical' }}
        />
      </div>
    </Modal>
  );
}

export default function AdminFeedbackPage() {
  const { t } = useTranslation();
  const { items, loading } = useFeedback();
  const { markAsRead } = useUnreadFeedback();
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((fb) => {
      if (typeFilter && fb.type !== typeFilter) return false;
      if (statusFilter && fb.status !== statusFilter) return false;
      if (priorityFilter && fb.priority !== priorityFilter) return false;
      if (q && !fb.title?.toLowerCase().includes(q) && !fb.submittedByName?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter, priorityFilter, search]);

  const newCount = useMemo(() => items.filter((fb) => fb.status === 'new').length, [items]);

  useEffect(() => {
    if (!loading) markAsRead();
  }, [loading, items.length, markAsRead]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>
            {t('feedback.inboxTitle')}
            {newCount > 0 && (
              <span className="fb-new-badge">{t('feedback.newCount', { count: newCount })}</span>
            )}
          </h1>
          <p className="page-header-sub">
            <MessageSquare size={13} /> {t('feedback.inboxSubtitle')}
          </p>
        </div>
      </div>

      <div className="page-content-wide">
        <div className="card">
          <div className="fb-toolbar">
            <div className="search-box fb-search">
              <Search size={15} />
              <input
                className="search-input"
                placeholder={t('feedback.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="fb-filter-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">{t('feedback.allTypes')}</option>
              {TYPES.map((typeKey) => (
                <option key={typeKey} value={typeKey}>{t(`feedback.types.${typeKey}`)}</option>
              ))}
            </select>

            <select
              className="fb-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t('feedback.allStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{t(`feedback.statuses.${s}`)}</option>
              ))}
            </select>

            <select
              className="fb-filter-select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">{t('feedback.allPriorities')}</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{t(`feedback.priorities.${p}`)}</option>
              ))}
            </select>

            {!loading && (
              <span className="fb-result-count">
                {t('feedback.resultCount', { filtered: filtered.length, total: items.length })}
              </span>
            )}
          </div>

          {loading ? (
            <div className="page-loading" style={{ minHeight: 220 }}>
              <div className="spinner" />
            </div>
          ) : items.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-12) var(--space-4)' }}>
              <MessageSquare size={32} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
              <p className="text-muted">{t('feedback.emptyAll')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-8) var(--space-4)' }}>
              <p className="text-muted">{t('feedback.emptyFiltered')}</p>
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table fb-table">
                <thead>
                  <tr>
                    <th className="fb-col-type">{t('feedback.type')}</th>
                    <th className="fb-col-title">{t('feedback.title')}</th>
                    <th className="fb-col-user">{t('users.displayName')}</th>
                    <th className="fb-col-priority">{t('feedback.priority')}</th>
                    <th className="fb-col-status">{t('users.status')}</th>
                    <th className="fb-col-date">{t('users.createdAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((fb) => {
                    const TypeIcon = TYPE_META[fb.type]?.icon ?? HelpCircle;
                    const created = fb.createdAt?.toDate?.();
                    return (
                      <tr
                        key={fb.id}
                        className="fb-row"
                        onClick={() => setSelected(fb)}
                        title={t('feedback.clickToView')}
                      >
                        <td className="fb-col-type">
                          <span className={`badge ${TYPE_META[fb.type]?.badge ?? ''}`}>
                            <TypeIcon size={11} />
                            {t(`feedback.types.${fb.type}`, { defaultValue: fb.type })}
                          </span>
                        </td>
                        <td className="fb-col-title">
                          <div className="fb-title-cell">
                            <span className="fb-title-text">{fb.title}</span>
                            <span className="fb-page-text">{fb.page}</span>
                          </div>
                        </td>
                        <td className="fb-col-user">
                          <div className="fb-user-cell">
                            <span className="fb-user-name">{fb.submittedByName || '—'}</span>
                            <span className="fb-user-role">{fb.submittedByRole}</span>
                          </div>
                        </td>
                        <td className="fb-col-priority">
                          <span className={`badge ${PRIORITY_META[fb.priority]?.badge ?? ''}`}>
                            {t(`feedback.priorities.${fb.priority}`, { defaultValue: fb.priority })}
                          </span>
                        </td>
                        <td className="fb-col-status">
                          <span className={`badge ${STATUS_META[fb.status]?.badge ?? ''}`}>
                            {t(`feedback.statuses.${fb.status}`, { defaultValue: fb.status })}
                          </span>
                        </td>
                        <td className="fb-col-date">
                          {created ? format(created, 'MMM d, yyyy') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <FeedbackDetail
          key={selected.id}
          item={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
